import { observable, reaction, computed, action, makeObservable, values, runInAction, toJS } from 'mobx'
import { parseShape, wrapPromise } from './utils';
import ShopItem from './ShopItem';
import { getSample } from 'utils/math';
import { GET_ACTIVE_DIGITALS, GET_ACTIVE_ITEMS, GET_CART_ITEMS, GET_DIGITAL_STATUS, GET_ITEM_STATUS, GET_ONE_DIGITAL, GET_ONE_ITEM } from 'api/graphql/shop';


class ShopStore {

    _items = [];
    loading = false;
    stale = true;
    allLoading = false;
    tag = null;

    constructor(model) {
        this.model = model;
        makeObservable(this, {
            model: false,
            _items: observable,
            loading: observable,
            allLoading: observable,
            tag: observable,
            setTag: action,
            items: computed,
            digitals: computed,
            allItems: computed
        })
    }

    setTag = (tag) => {
        this.tag = tag;
    }

    get items() {
        if (this.tag) {
            return this._items.filter(i => !i.isDigital && i.isActive && i.tags.includes(this.tag));
        } else {
            return this._items.filter(i => !i.isDigital && i.isActive);
        }
    }

    get digitals() {
        if (this.tag) {
            return this._items.filter(i => i.isDigital && i.isActive && i.tags.includes(this.tag));
        } else {
            return this._items.filter(i => i.isDigital && i.isActive);
        }
    }

    get allItems() {
        if (this.tag) {
            return this._items.filter(i => i.isActive && i.tags.includes(this.tag));
        } else {
            return this._items.filter(i => i.isActive);
        }
    }

    get activeItemsData() {
        if (this.allItems.length > 0 && !this.stale) {
            return { read: () => this.allItems } ;
        }
        this.stale = false;
        return wrapPromise(this.getActiveItems());
    }

    get allItemsData() {
        if (this.items.length > 0) {
            return { read: () => this.items } ;
        }
        return wrapPromise(this.getAllItems());
    }

    oneItemData = (slug) => {
        const item = this.getShopItemBySlug(slug);
        if (item) {
            this.updateItemStatus(slug);
            return { read: () => item }
        }
        return wrapPromise(this.getOneItem(slug));
    }

    get allDigitalsData() {
        if (this.digitals.length > 0) {
            return { read: () => this.digitals } ;
        }
        return wrapPromise(this.getAllDigitals());
    }

    oneDigitalData = (slug) => {
        const item = this.getShopItemBySlug(slug);
        if (item) {
            this.updateDigitalStatus(slug);
            return { read: () => item }
        }
        return wrapPromise(this.getOneDigital(slug));
    }

    get cartItemsData() {
        return wrapPromise(this.getCartItems());
    }

    getActiveItems = () => {
        const promises = [
            this.model.apollo.query( { query: GET_ACTIVE_ITEMS }),
            this.model.apollo.query( { query: GET_ACTIVE_DIGITALS }),
        ];
        return Promise.all(promises).then(([response1, response2]) => {
            const activeItems = response1.data;
            const activeDigitals = response2.data;
            runInAction(() => {
                const parsedItems = this.model.kz.isKZ ? [] : parseShape(activeItems)['allItem'];
                const parsedDigitals = parseShape(activeDigitals)['allDigital'];
                parsedItems.forEach(json => this.updateShopItemsFromServer({...json, isDigital: false}));
                parsedDigitals.forEach(json => this.updateShopItemsFromServer({ ...json, isDigital: true }));
            });
            if (this.tag) {
                return this._items.filter(i => i.isActive && i.tags.includes(this.tag));
            } else {
                return this._items.filter(i => i.isActive);
            }
        });
    }

    getAllItems = () => {
        return this.model.apollo.query( { query: GET_ACTIVE_ITEMS } )
            .then((response) => {
                const data = response.data;
                runInAction(() => {
                    const parsed = parseShape(data)['allItem'];
                    parsed.forEach(json => this.updateShopItemsFromServer({ ...json, isDigital: false }))
                });
                return this._items.filter(i => i.isActive);
            });
    }

    getAllDigitals = () => {
        return this.model.apollo.query( { query: GET_ACTIVE_DIGITALS } )
            .then((response) => {
                const data = response.data;
                runInAction(() => {
                    const parsed = parseShape(data)['allDigital'];
                    parsed.forEach(json => this.updateShopItemsFromServer({ ...json, isDigital: true }))
                });
                return this._items.filter(i => i.isActive);
            });
    }

    getOneItem = (slug) => {
        return this.model.apollo.query( {
            query: GET_ONE_ITEM,
            variables: { url: slug }
        } ).then((response) => {
            const data = response.data;
            const parsed = parseShape(data)['allItem'];
            runInAction(() => {
                parsed.forEach(json => this.updateShopItemsFromServer({ ...json, isDigital: false }))
            })
            return this.getShopItemBySlug(slug);
        }).catch(err => {
            throw new Error(err)
        });
    }

    updateItemStatus = (slug) => {
        return this.model.apollo.query( {
            query: GET_ITEM_STATUS,
            variables: { url: slug }
        } ).then((response) => {
            const data = response.data;
            const parsed = parseShape(data)['allItem']
            runInAction(() => {
                parsed.forEach(json => this.updateShopItemsFromServer(json))
            });
        }).catch(err => {
            throw new Error(err)
        });
    }

    getOneDigital = (slug) => {
        return this.model.apollo.query( {
            query: GET_ONE_DIGITAL,
            variables: { url: slug }
        } ).then((response) => {
            const data = response.data;
            const parsed = parseShape(data)['allDigital'];
            runInAction(() => {
                parsed.forEach(json => this.updateShopItemsFromServer({ ...json, isDigital: true }))
            });
            return this.getShopItemBySlug(slug);
        }).catch(err => {
            throw new Error(err)
        });
    }

    updateDigitalStatus = (slug) => {
        return this.model.apollo.query( {
            query: GET_DIGITAL_STATUS,
            variables: { url: slug }
        } ).then((response) => {
            const data = response.data;
            const parsed = parseShape(data)['allDigital']
            runInAction(() => {
                parsed.forEach(json => this.updateShopItemsFromServer(json))
            })
        }).catch(err => {
            throw new Error(err)
        });
    }

    getCartItems = () => {
        return this.model.apollo.query( { query: GET_CART_ITEMS, } )
            .then((response) => {
                const data = response.data;
                runInAction(() => {
                    const parsed = parseShape(data)['allItem'];
                    parsed.forEach(json => this.updateShopItemsFromServer({ ...json, isDigital: false }))
                });
                return this._items;
            });
    }

    updateShopItemsFromServer(json) {
        let item = this._items.find(item => item.id === json.id)
        if (!item) {
            item = new ShopItem(this, json)
            this._items.push(item);
            this._items.sort((item1, item2) => item2.data.points - item1.data.points);
        } else {
            item.updateFromJson(json)
        }
    }

    getShopItemBySlug = (slug) => {
        return this._items.find(item => item.data.url === slug);
    }

    getItemsSample = (exclude, size = 4) => {
        const items = this._items.filter(item => !item.data.isDigital && !exclude.includes(item.data.url) && item.isActive);
        return getSample(items, size);
    }

    itemsSampleData = (exclude, size = 4) => {
        if (this.items.length === 1 && exclude.includes(this.items[0].data.url)) {
            const promise = this.getAllItems().then(() => this.getItemsSample(exclude, size));
            return wrapPromise(promise);
        } else {
            return {
                read: () => this.getItemsSample(exclude, size)
            }
        }
    }

    getDigitalsSample = (exclude, size = 4) => {
        const digitals = this._items.filter(item => item.data.isDigital && !exclude.includes(item.data.url) && item.isActive);
        return getSample(digitals, size);
    }

    digitalsSampleData = (exclude, size = 4) => {
        if (this.digitals.length === 1 && exclude.includes(this.digitals[0].data.url)) {
            const promise = this.getAllDigitals().then(() => this.getDigitalsSample(exclude, size));
            return wrapPromise(promise);
        } else {
            return {
                read: () => this.getDigitalsSample(exclude, size)
            }
        }
    }

}

export default ShopStore;
